import { createContext, useCallback, useContext, useState } from "react";
import type { ReactNode } from "react";

type MemberCardState = {
  /** Member being shown, or null when the card is closed. */
  memberId: string | null;
  /** Shown while the record loads, so the card can open with a name already
   *  on it instead of a blank frame. Every call site already has the name. */
  fallbackName?: string;
};

type MemberCardContextType = {
  state: MemberCardState;
  openMemberCard: (memberId: string, fallbackName?: string) => void;
  closeMemberCard: () => void;
};

const MemberCardContext = createContext<MemberCardContextType | null>(null);

/**
 * Holds whichever member's visiting card is open.
 *
 * A context rather than local state in each caller: member names appear in
 * contract roles, party signatories, audit rows, comments and listings, and
 * every one of those would otherwise have to own modal state and mount its
 * own copy of the card. One provider, one mounted card, and `MemberLink`
 * anywhere — the same shape as PreviewProvider/GlobalPreviewModal.
 */
export function MemberCardProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<MemberCardState>({ memberId: null });

  const openMemberCard = useCallback((memberId: string, fallbackName?: string) => {
    setState({ memberId, fallbackName });
  }, []);

  const closeMemberCard = useCallback(() => {
    setState({ memberId: null });
  }, []);

  return (
    <MemberCardContext.Provider
      value={{ state, openMemberCard, closeMemberCard }}
    >
      {children}
    </MemberCardContext.Provider>
  );
}

/**
 * Null-safe on purpose: MemberLink renders in trees that are also mounted in
 * isolation (the document editor's own preview, tests), and a missing provider
 * should degrade to plain text rather than crash the page.
 */
export function useMemberCard() {
  return useContext(MemberCardContext);
}
